import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, CircularProgress, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import supabase from '../supabaseClient';
import axios from 'axios';
import API_BASE from '../config';
import { useAuth } from '../contexts/AuthContext';
import cytoscape from 'cytoscape';

const HealthCanvas = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [lines, setLines] = useState([]);
  const [nodeLines, setNodeLines] = useState([]);
  const [elements, setElements] = useState([]);
  const cyRef = useRef(null);
  const containerRef = useRef(null);
  const [managing, setManaging] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null); // { id, label }

  const formatTs = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleString(undefined, {
      month: 'numeric', day: 'numeric', year: '2-digit',
      hour: 'numeric', minute: '2-digit',
    });
  };

  useEffect(() => {
    let cancelled = false;
    async function loadGraph() {
      if (!user) { setLoading(false); setLines([]); return; }
      setLoading(true);
      try {
        // Try backend endpoint first (uses service role, avoids RLS issues)
        const { data: { session } } = await supabase.auth.getSession();
        const accessToken = session?.access_token || '';
        let edges = [];
        let nodes = [];
        try {
          const resp = await axios.get(`${API_BASE}/health-graph`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
          });
          const payload = resp?.data || {};
          edges = Array.isArray(payload.edges) ? payload.edges : [];
          nodes = Array.isArray(payload.nodes) ? payload.nodes : [];
        } catch (_) {
          // Fallback to direct Supabase (requires proper RLS policies)
          const edgeRes = await supabase
          .from('graph_edges')
          .select('id, src, dst, edge_type, weight, created_at, updated_at')
          .order('updated_at', { ascending: false })
          .limit(100);
          if (edgeRes.error) throw edgeRes.error;
          edges = edgeRes.data || [];
          const ids = Array.from(new Set(edges.flatMap(e => [e.src, e.dst])));
          if (!ids.length) { if (!cancelled) { setLines([]); setLoading(false); } return; }
          const nodeRes = await supabase
            .from('graph_nodes')
            .select('id, node_type, label')
            .in('id', ids);
          if (nodeRes.error) throw nodeRes.error;
          nodes = nodeRes.data || [];
        }
        const byId = new Map((nodes || []).map(n => [n.id, n]));
        const out = (edges || []).map(e => {
          const a = byId.get(e.src) || { label: 'unknown' };
          const b = byId.get(e.dst) || { label: 'unknown' };
          const date = e.updated_at || e.created_at;
          let text = '';
          if (e.edge_type === 'experiences' || e.edge_type === 'treats' || e.edge_type === 'has_age' || e.edge_type === 'has_gender') {
            text = `${a.label} → ${b.label}`;
          } else if (e.edge_type === 'co_occurs') {
            text = `${a.label} ↔ ${b.label}`;
          } else {
            text = `${a.label} - ${e.edge_type} - ${b.label}`;
          }
          return {
            ts: date ? new Date(date).getTime() : 0,
            date: formatTs(date),
            weight: e.weight || 1,
            text,
          };
        });
        if (!cancelled) setLines(out);

        // Also list recent nodes (independent of edges) for the signed-in user
        // Fetch via backend (service role) to include timestamps and avoid RLS issues
        try {
          const nodesResp = await axios.get(`${API_BASE}/health-nodes`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
          });
          const nlist = Array.isArray(nodesResp.data) ? nodesResp.data : [];
          const nl = nlist.map(n => {
            const nd = n.updated_at || n.created_at;
            return {
              id: n.id,
              ts: nd ? new Date(nd).getTime() : 0,
              date: formatTs(nd),
              type: n.node_type,
              label: n.label,
            };
          });
          if (!cancelled) setNodeLines(nl);
        } catch (_) {
          // Non-fatal: leave previous nodeLines if request fails
        }

        // Build Cytoscape elements: prefer all-users dataset for visualization
        try {
          const allResp = await axios.get(`${API_BASE}/health-graph-all`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
          });
          const all = allResp?.data || {};
          const allNodes = Array.isArray(all.nodes) ? all.nodes : [];
          const allEdges = Array.isArray(all.edges) ? all.edges : [];

          // Aggregate across users by normalized label + type so identical
          // concepts collapse even if node IDs differ per user.
          const norm = (s) => String(s || '').trim().toLowerCase();

          // Map nodeId -> { type, label, key }
          const byId = new Map(
            allNodes.map(n => [String(n.id), { type: String(n.node_type || ''), label: String(n.label || ''), key: `${String(n.node_type || '')}:${norm(n.label)}` }])
          );

          // Aggregate edges by (srcKey, dstKey, type)
          const edgeAgg = new Map();
          for (const e of allEdges) {
            const s = byId.get(String(e.src));
            const t = byId.get(String(e.dst));
            if (!s || !t) continue;
            const key = `${s.key}|${t.key}|${e.edge_type}`;
            const w = Number(e.weight) || 1;
            const cur = edgeAgg.get(key) || { source: s.key, target: t.key, type: e.edge_type, weight: 0 };
            cur.weight += w;
            edgeAgg.set(key, cur);
          }

          // Build collapsed node list from keys we actually use
          const nodeSet = new Map();
          for (const val of edgeAgg.values()) {
            const [sType, sLabel] = val.source.split(':');
            const [tType, tLabel] = val.target.split(':');
            nodeSet.set(val.source, { id: val.source, type: sType, label: sLabel });
            nodeSet.set(val.target, { id: val.target, type: tType, label: tLabel });
          }

          const cyAllEdges = Array.from(edgeAgg.values()).map((e, i) => ({ data: { id: `agg-${i}`, source: e.source, target: e.target, type: e.type, weight: e.weight } }));
          // Compute degrees for sizing
          const deg = new Map();
          for (const ed of cyAllEdges) {
            const s = ed.data.source; const t = ed.data.target;
            deg.set(s, (deg.get(s) || 0) + 1);
            deg.set(t, (deg.get(t) || 0) + 1);
          }
          const cyAllNodes = Array.from(nodeSet.values()).map(n => ({ data: { id: n.id, label: n.label, type: n.type, degree: deg.get(n.id) || 0 } }));

          if (!cancelled && (cyAllNodes.length || cyAllEdges.length)) {
            setElements([...cyAllNodes, ...cyAllEdges]);
          } else {
            // Fallback to user-only graph if all-users is empty
            const cyEdges = (edges || []).map(e => ({ data: { id: `e-${e.id}`, source: String(e.src), target: String(e.dst), type: e.edge_type, weight: e.weight || 1 } }));
            const deg2 = new Map();
            for (const ed of cyEdges) {
              const s = ed.data.source; const t = ed.data.target;
              deg2.set(s, (deg2.get(s) || 0) + 1);
              deg2.set(t, (deg2.get(t) || 0) + 1);
            }
            const cyNodes = (nodes || []).map(n => ({ data: { id: String(n.id), label: n.label, type: n.node_type, degree: deg2.get(String(n.id)) || 0 } }));
            if (!cancelled) setElements([...cyNodes, ...cyEdges]);
          }
        } catch (_) {
          // Fallback to user-only graph
          const cyEdges = (edges || []).map(e => ({ data: { id: `e-${e.id}`, source: String(e.src), target: String(e.dst), type: e.edge_type, weight: e.weight || 1 } }));
          const deg2 = new Map();
          for (const ed of cyEdges) {
            const s = ed.data.source; const t = ed.data.target;
            deg2.set(s, (deg2.get(s) || 0) + 1);
            deg2.set(t, (deg2.get(t) || 0) + 1);
          }
          const cyNodes = (nodes || []).map(n => ({ data: { id: String(n.id), label: n.label, type: n.node_type, degree: deg2.get(String(n.id)) || 0 } }));
          if (!cancelled) setElements([...cyNodes, ...cyEdges]);
        }
      } catch (err) {
        console.error('Error loading graph:', err);
        if (!cancelled) setLines([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadGraph();
    return () => { cancelled = true; };
  }, [user]);

  // Initialize / update Cytoscape instance when data or module changes
  useEffect(() => {
    if (!containerRef.current) return;
    // Destroy existing instance to avoid leaks
    if (cyRef.current) {
      try { cyRef.current.destroy(); } catch (_) {}
      cyRef.current = null;
    }
    const cy = cytoscape({
      container: containerRef.current,
      elements: elements,
      style: [
        { selector: 'node', style: {
            label: 'data(label)',
            'font-size': 10,
            'text-wrap': 'wrap',
            'text-max-width': 80,
            'text-valign': 'center',
            'text-halign': 'center',
            'color': '#FFFFFF',
            // Size nodes by degree (clamped 0..20 → 28..70px)
            width: 'mapData(degree, 0, 20, 28, 70)',
            height: 'mapData(degree, 0, 20, 28, 70)',
            'background-color': '#90caf9', // default
          }},
        { selector: 'node[type = "condition"]', style: { 'background-color': '#64b5f6' } },
        { selector: 'node[type = "symptom"]',   style: { 'background-color': '#ffcc80' } },
        { selector: 'node[type = "med"]',       style: { 'background-color': '#a5d6a7' } },
        { selector: 'node[type = "profile"]',   style: { 'background-color': '#bdbdbd' } },
        { selector: 'edge', style: {
            // Map edge weight to stroke thickness; values outside range clamp.
            width: 'mapData(weight, 1, 10, 1, 8)',
            'line-color': '#b0bec5',
            'target-arrow-color': '#b0bec5',
            'target-arrow-shape': 'triangle',
            'curve-style': 'bezier',
          }},
        { selector: 'edge[type = "co_occurs"]', style: {
            'target-arrow-shape': 'none',
            'line-style': 'dashed',
          }},
      ],
      layout: { name: 'cose', fit: true, animate: false },
    });
    cyRef.current = cy;
    // Fit after a tick to account for container sizing
    setTimeout(() => { try { cy.resize(); cy.fit(undefined, 20); } catch (_) {} }, 0);
    return () => {
      try { cy.destroy(); } catch (_) {}
      cyRef.current = null;
    };
  }, [elements]);

  if (!user) {
    return (
      <Box sx={{ p: 2, maxWidth: 720, mx: 'auto' }}>
        <Typography variant="h4" gutterBottom>Health Canvas</Typography>
        <Typography sx={{ color: 'text.secondary', mb: 2 }}>
          Please sign in to view your health graph.
        </Typography>
        <Button variant="outlined" href="/auth">Sign in</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 720, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Health Canvas
      </Typography>
      {/* Graph viewport */}
      <Box sx={{ position: 'relative', mb: 2 }}>
        <Box ref={containerRef} sx={{ width: '100%', height: 420, borderRadius: 1, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }} />
        <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => { try { cyRef.current?.fit(undefined, 20); } catch (_) {} }}
            sx={{ bgcolor: '#A7CF83', color: '#000000', '&:hover': { bgcolor: '#97bf73' } }}
          >
            Fit
          </Button>
        </Box>
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Loading your health data…</Typography>
        </Box>
      ) : lines.length === 0 ? (
        <Box sx={{ color: 'text.secondary', mt: 1 }}>No health data available</Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
          <Typography variant="subtitle2" sx={{ mt: 1, color: 'text.secondary' }}>
            Relationships
          </Typography>
          {lines
            .sort((a, b) => (b.ts || 0) - (a.ts || 0))
            .map((item, idx) => (
              <Box key={idx} sx={{ display: 'grid', gridTemplateColumns: '1fr auto', columnGap: 2, alignItems: 'baseline' }}>
                <Typography variant="body2">
                  • {item.text} {item.weight > 1 ? `(x${item.weight})` : ''}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', pl: 2 }}>
                  {item.date || ''}
                </Typography>
              </Box>
            ))}

          <Box sx={{ mt: 2, mb: 0.5, borderTop: '1px solid', borderColor: 'divider' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" sx={{ mt: 1, color: 'text.secondary' }}>
              Recorded Data
            </Typography>
            {managing && (
              <Button
                variant="outlined"
                onClick={() => setManaging(false)}
                sx={{ color: '#EAD7B2', borderColor: '#EAD7B2', '&:hover': { borderColor: '#EAD7B2', backgroundColor: 'rgba(234, 215, 178, 0.1)' } }}
              >
                Done managing data
              </Button>
            )}
          </Box>
          {nodeLines
            .sort((a, b) => (b.ts || 0) - (a.ts || 0))
            .map((n, i) => (
              <Box key={n.id || i} sx={{ display: 'grid', gridTemplateColumns: managing ? '1fr auto auto' : '1fr auto', columnGap: 2, alignItems: 'center' }}>
                <Typography variant="body2">• [{n.type}] {n.label}</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', pl: 2 }}>{n.date || ''}</Typography>
                {managing && (
                  <IconButton size="small" color="error" aria-label={`Delete ${n.label}`} onClick={() => { setPendingDelete({ id: n.id, label: n.label }); setConfirmOpen(true); }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
          {/* Manage data toggle at bottom */}
          {!managing && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setManaging(true)}
                sx={{ color: '#EAD7B2', borderColor: '#EAD7B2', '&:hover': { borderColor: '#EAD7B2', backgroundColor: 'rgba(234, 215, 178, 0.1)' } }}
              >
                Manage data
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Confirm deletion dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Delete data?</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this data{pendingDelete?.label ? `: “${pendingDelete.label}”` : ''}? It cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={async () => {
            try {
              const { data: { session } } = await supabase.auth.getSession();
              const accessToken = session?.access_token || '';
              await axios.delete(`${API_BASE}/health-nodes/${pendingDelete?.id}`, { headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {} });
              setNodeLines(prev => prev.filter(x => x.id !== pendingDelete?.id));
              setConfirmOpen(false);
              setPendingDelete(null);
            } catch (e) {
              console.error('Delete failed', e);
              setConfirmOpen(false);
            }
          }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HealthCanvas;
